import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Archive,
  Bell,
  CheckCheck,
  Loader2,
  MailWarning,
  RefreshCw,
  ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

type Severity = "info" | "warning" | "error";

type NotificationRow = {
  id: string;
  kind: string;
  severity: Severity;
  title: string;
  body: string | null;
  source: string | null;
  related_table: string | null;
  related_id: string | null;
  details: Record<string, unknown> | null;
  read_at: string | null;
  archived_at: string | null;
  created_at: string;
};

const SEVERITY_TONE: Record<Severity, string> = {
  info: "bg-sky-500/15 text-sky-600 border-sky-500/30",
  warning: "bg-amber-500/15 text-amber-600 border-amber-500/30",
  error: "bg-destructive/15 text-destructive border-destructive/30",
};

const KIND_LABEL: Record<string, string> = {
  testimonial_submitted: "Testimonial submitted",
  testimonial_submit_failed: "Submission failed",
};

const fmt = (value: string | null) =>
  value ? new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

const FILTERS = ["unread", "all", "archived"] as const;
type Filter = (typeof FILTERS)[number];

const AdminNotifications = () => {
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [rows, setRows] = useState<NotificationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("unread");

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setIsAdmin(false);
      return;
    }
    // Admins see everything; escalation-role members pass the gate but the
    // database only returns the critical alerts addressed to them.
    Promise.all([
      supabase.rpc("has_role", { _user_id: user.id, _role: "admin" }),
      supabase.rpc("has_role", { _user_id: user.id, _role: "escalation" }),
    ]).then(([a, e]) => setIsAdmin(a.data === true || e.data === true));
  }, [authLoading, user]);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("admin_notifications")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(300);
    if (error) toast.error(error.message);
    setRows((data as NotificationRow[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin, load]);

  // Live updates so a new intake shows without a refresh.
  useEffect(() => {
    if (!isAdmin) return;
    const channel = supabase
      .channel("admin-notifications")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "admin_notifications" },
        (payload) => {
          const row = payload.new as NotificationRow;
          setRows((prev) => (prev.some((r) => r.id === row.id) ? prev : [row, ...prev]));
          toast(row.title, { description: row.body?.slice(0, 140) });
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [isAdmin]);

  const visible = useMemo(() => {
    if (filter === "archived") return rows.filter((r) => r.archived_at);
    if (filter === "unread") return rows.filter((r) => !r.read_at && !r.archived_at);
    return rows.filter((r) => !r.archived_at);
  }, [rows, filter]);

  const unreadCount = rows.filter((r) => !r.read_at && !r.archived_at).length;

  const patch = async (
    id: string,
    values: { read_at?: string; read_by?: string | null; archived_at?: string },
  ) => {
    setBusyId(id);
    const { error } = await supabase.from("admin_notifications").update(values).eq("id", id);
    if (error) toast.error(error.message);
    else setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...values } : r)));
    setBusyId(null);
  };

  const markAllRead = async () => {
    const ids = rows.filter((r) => !r.read_at && !r.archived_at).map((r) => r.id);
    if (!ids.length) return;
    const stamp = new Date().toISOString();
    const { error } = await supabase
      .from("admin_notifications")
      .update({ read_at: stamp, read_by: user?.id ?? null })
      .in("id", ids);
    if (error) return toast.error(error.message);
    setRows((prev) => prev.map((r) => (ids.includes(r.id) ? { ...r, read_at: stamp } : r)));
    toast.success("All notifications marked read.");
  };

  if (authLoading || isAdmin === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-destructive" aria-hidden />
              Admin access required
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground">
            <p>This notification inbox is limited to administrators.</p>
            <Button asChild>
              <Link to="/auth?next=%2Fadmin%2Fnotifications">Sign in</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Admin notifications — Dr. Sully Speaks</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <main className="container mx-auto max-w-4xl px-4 py-12">
        <header className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-2 font-display text-3xl font-bold tracking-tight">
              <Bell className="h-6 w-6 text-primary" aria-hidden />
              Notifications
              {unreadCount > 0 && (
                <Badge className="ml-1 bg-primary text-primary-foreground">{unreadCount} new</Badge>
              )}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Every testimonial intake — successful or failed — lands here in real time.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} aria-hidden />
              Refresh
            </Button>
            <Button size="sm" onClick={() => void markAllRead()} disabled={!unreadCount}>
              <CheckCheck className="mr-2 h-4 w-4" aria-hidden />
              Mark all read
            </Button>
          </div>
        </header>

        <Tabs value={filter} onValueChange={(v) => setFilter(v as Filter)} className="mb-6">
          <TabsList>
            <TabsTrigger value="unread">Unread</TabsTrigger>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="archived">Archived</TabsTrigger>
          </TabsList>
        </Tabs>

        {visible.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-sm text-muted-foreground">
              Nothing here right now.
            </CardContent>
          </Card>
        ) : (
          <ul className="space-y-3">
            {visible.map((row) => {
              const details = (row.details ?? {}) as Record<string, unknown>;
              const email = typeof details.email === "string" ? details.email : "";
              const fields = Array.isArray(details.fields) ? (details.fields as string[]) : [];
              return (
                <li key={row.id}>
                  <Card className={row.read_at ? "opacity-80" : "border-primary/40"}>
                    <CardHeader className="pb-3">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <CardTitle className="flex items-center gap-2 text-base">
                          {row.severity === "info" ? (
                            <Bell className="h-4 w-4 text-sky-600" aria-hidden />
                          ) : (
                            <AlertTriangle className="h-4 w-4 text-amber-600" aria-hidden />
                          )}
                          {row.title}
                        </CardTitle>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={SEVERITY_TONE[row.severity]}>
                            {KIND_LABEL[row.kind] ?? row.kind}
                          </Badge>
                          <span className="text-xs text-muted-foreground">{fmt(row.created_at)}</span>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                      {row.body && <p className="text-muted-foreground">{row.body}</p>}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        {email && <span>Email: {email}</span>}
                        {fields.length > 0 && <span>Fields: {fields.join(", ")}</span>}
                        {row.source && <span>Source: {row.source}</span>}
                      </div>
                      <div className="flex flex-wrap gap-2 pt-1">
                        {row.related_table === "testimonial_submissions" && row.related_id && (
                          <Button asChild size="sm" variant="secondary">
                            <Link to="/admin/testimonials">Open moderation queue</Link>
                          </Button>
                        )}
                        {!row.read_at && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busyId === row.id}
                            onClick={() =>
                              void patch(row.id, {
                                read_at: new Date().toISOString(),
                                // read_by is set server-side by policy-safe update below
                              })
                            }
                          >
                            <CheckCheck className="mr-2 h-4 w-4" aria-hidden />
                            Mark read
                          </Button>
                        )}
                        {!row.archived_at && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={busyId === row.id}
                            onClick={() => void patch(row.id, { archived_at: new Date().toISOString() })}
                          >
                            <Archive className="mr-2 h-4 w-4" aria-hidden />
                            Archive
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </li>
              );
            })}
          </ul>
        )}

        <p className="mt-8 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-700">
          <MailWarning className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          Email alerts to admins are ready to switch on as soon as a verified sender domain is set up
          for drsullyspeaks.com. Until then, notifications appear here only.
        </p>
      </main>
    </div>
  );
};

export default AdminNotifications;
